import React from 'react';
import { Helmet } from 'react-helmet';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { navigate, RouteComponentProps } from '@reach/router';
import {
  addProjectMember,
  addProjectMemberFn,
  getProject,
  getProjectFn,
  removeProjectMember,
  removeProjectMemberFn,
  updateProjectMember,
  updateProjectMemberFn,
} from 'store/companies/companiesSlice';
import { IUsers, RootState } from 'store/store';
import messages from './Messages';
import {
  NoUserExists,
  ProjectMembersHeader,
  ProjectMembersListWrapper,
  ProjectMembersTabs,
  ProjectMembersWrapper,
  ProjectTabItem,
} from './styles';
import ProjectMemberView from './ProjectMemberView';
import { IOption } from 'components/Select/Select';
import { display } from 'components/ErrorView/ErrorDialog';
import LogoLoading from 'components/LogoLoading';
import notify from 'components/Notify';
import { projectRoutesName } from 'components/MainHeader/Messages';
import ProjectMembersSelect from './ProjectMembersSelect/ProjectMembersSelect';
import ProjectMemberBatchAdd from './ProjectMemberBatchAdd';
import APopover from 'components/APopover';
import Button from 'components/Button/Button';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import Select from 'components/Select';
import { appDispatch } from 'store/store';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import {
  ICompany,
  ICompanyMember,
  ICompanyRole,
  IProjectMemberView,
  IProjectStore,
  ProjectMemberConfig,
} from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { IUser } from 'store/users/usersType';
import { isCompanyOwnerOrSupport } from 'store/companies/companiesUtils';
import i18n from '../../../i18n';

interface IStateToProps {
  companyMembers: { [keys: number]: ICompanyMember };
  company: ICompany;
  companyRoles: { [keys: number]: ICompanyRole };
  companyOwnerId: number;
  projectMembers: any;
  projectRoleMembers: any;
  myMembership?: IProjectMemberView;
  project: IProjectStore;
  projectName: string;
  thisUser: IThisUserState;
  users: IUsers['map'];
}

interface IDispatchToProps {
  addMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof addProjectMemberFn>;
  removeMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof removeProjectMemberFn>;
  updateMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof updateProjectMemberFn>;
  getProjectData: (
    projectId: number,
    companyId: number
  ) => ReturnType<typeof getProjectFn>;
  requireUsers: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
}

interface IOwnProps {
  companyId?: string;
  projectId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;
export interface BatchAddItem {
  user?: IUser;
  name: string;
  title: string;
  relatedRole?: ICompanyRole;
  relatedRoleTitle?: string;
  projectRole?: ICompanyRole;
  addRole?: boolean;
  isAdmin?: boolean;
  canCreate?: boolean;
  visibleAllTasks?: boolean;
}

interface IComponentStates {
  loading: boolean;
  searchTerm: string;
  activeTab: 'explicit' | 'implicit' | 'batchAdd';
  batchAddInfo: BatchAddItem[];
}

const projectMemberItemTabs: Array<IComponentStates['activeTab']> = [
  'explicit',
  'implicit',
  'batchAdd',
];

class ProjectMembersList extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      loading: true,
      searchTerm: '',
      activeTab: 'explicit',
      batchAddInfo: [],
    };
  }

  componentDidMount() {
    if (
      !this.props.company.members[Number(this.props.thisUser.userId)].admin &&
      !this.props.myMembership?.admin
    ) {
      navigate('/homepage').finally();
    }
    const projectId = Number(this.props.projectId);
    this.props
      .getProjectData(projectId, this.props.company.id)
      .then(() => {
        const companyMembers = Object.keys(this.props.companyMembers).map(
          (it) => Number(it)
        );

        return this.props.requireUsers(companyMembers);
      })
      .then(() => {
        this.setState({
          loading: false,
        });
      })
      .catch((err: any) => {
        display(err);
      });
  }

  handleDeleteMember = (type: 'userId' | 'roleId') => (memberId: number) => {
    const projectMember =
      type === 'userId'
        ? {
            userId: memberId,
            projectId: Number(this.props.projectId),
          }
        : {
            roleId: memberId,
            projectId: Number(this.props.projectId),
          };
    this.props
      .removeMember(projectMember as IProjectMemberView, this.props.company.id)
      .then(
        () => {
          notify(i18n.t('memberRemoved', { ns: 'companyManagement' }));
        },
        (err: any) => {
          display(err);
        }
      );
  };

  handleUpdateMemberStatus =
    (memberGroup: IProjectMemberView) =>
    (
      memberId: number,
      type: ProjectMemberConfig,
      value: string | boolean | number | null
    ) => {
      const member = { ...memberGroup[memberId.toString()], [type]: value };
      return this.props.updateMember(member, this.props.company.id);
    };

  handleBatchAdd = (users: BatchAddItem[]) => {
    if (users.length > 0) {
      this.setState({ activeTab: 'batchAdd', batchAddInfo: users });
    } else {
      this.setState({ activeTab: 'explicit', batchAddInfo: [] });
    }
  };
  handleSubmitBatchAdd = async (
    isAdmin: boolean,
    canCreate: boolean,
    visibleAllTask: boolean
  ) => {
    const { batchAddInfo } = this.state;
    const { projectMembers, projectRoleMembers } = this.props;
    if (batchAddInfo.length > 0) {
      const promises: any = [];
      let stay = false;
      batchAddInfo.forEach((newUser) => {
        if (!newUser.user && !(newUser.relatedRole && newUser.addRole)) {
          stay = true;
          return;
        }
        const exists = newUser.addRole
          ? projectRoleMembers[newUser.relatedRole!.id.toString()]
          : projectMembers[newUser.user!.id.toString()];
        if (exists) {
          stay = true;
          return;
        }
        promises.push(
          this.props
            .addMember(
              {
                admin: newUser.isAdmin || isAdmin,
                canCreate: newUser.canCreate || canCreate,
                hidden: false,
                implicit: false,
                projectId: Number(this.props.projectId!),
                title: newUser.projectRole ? undefined : newUser.title.trim(),
                userId: newUser.addRole ? undefined : newUser.user!.id,
                roleId: newUser.addRole ? newUser.relatedRole!.id : undefined,
                projectRoleId: newUser.projectRole?.id,
                visibleAllTasks: newUser.visibleAllTasks || visibleAllTask,
                relatedRoleId: newUser.relatedRole?.id,
              },
              this.props.company.id
            )
            .then(() => {
              let newBatchAddInfo = [...this.state.batchAddInfo];
              newBatchAddInfo = newBatchAddInfo.filter((it) =>
                it.addRole
                  ? !(it.relatedRole?.id === newUser.relatedRole?.id)
                  : !(it.user === newUser.user && it.user)
              );
              this.setState({ batchAddInfo: newBatchAddInfo });
            })
        );
      });
      return Promise.all(promises).then(() => {
        if (!stay) this.setState({ activeTab: 'explicit' });
        else
          notify(
            i18n.t('pleaseCheckTheseUsers', { ns: 'companyManagement' }),
            'warning'
          );
      });
    }
    return Promise.resolve();
  };
  handleSelectUser = (values: IOption[]) => {
    let currentProjectMember: IProjectMemberView | undefined;
    const newMember = values.find((option) => {
      currentProjectMember = this.props.projectMembers[option.value.toString()];
      return !currentProjectMember;
    });
    if (newMember) {
      const projectMember = {
        userId: newMember.value,
        projectId: Number(this.props.projectId),
      };
      this.props
        .addMember(projectMember as IProjectMemberView, this.props.company.id)
        .then(() => {
          this.setState({ activeTab: 'explicit' });
          notify(messages.memberAddedToProject(newMember.label), 'successful');
        })
        .catch((err: any) => {
          display(err);
        });
    } else if (currentProjectMember) {
      notify(
        messages.thisUserIsCurrentlyMemberOfTheProject(
          currentProjectMember.implicit
        ),
        'warning'
      );
    }
  };
  handleSelectRole = (values: IOption[]) => {
    let currentProjectMember: IProjectMemberView | undefined;
    const newMember = values.find((option) => {
      currentProjectMember =
        this.props.projectRoleMembers[option.value.toString()];
      return !currentProjectMember;
    });
    if (newMember) {
      const projectMember = {
        roleId: newMember.value,
        projectId: Number(this.props.projectId),
      };
      this.props
        .addMember(projectMember as IProjectMemberView, this.props.company.id)
        .then(() => {
          this.setState({ activeTab: 'explicit' });
          notify(messages.memberAddedToProject(newMember.label), 'successful');
        })
        .catch((err: any) => {
          display(err);
        });
    } else if (currentProjectMember) {
      notify(
        messages.thisUserIsCurrentlyMemberOfTheProject(
          currentProjectMember.implicit,
          true
        ),
        'warning'
      );
    }
  };

  handleChangeActiveTab = (activeTab: IComponentStates['activeTab']) => () => {
    this.setState({ activeTab });
  };

  render() {
    const notOwnerButIsProjectAdmin =
      this.props.companyOwnerId !== Number(this.props.thisUser.userId) &&
      this.props.myMembership?.admin;

    const { activeTab, loading, batchAddInfo } = this.state;

    if (loading || this.props.project.stub) {
      return <LogoLoading />;
    }
    const {
      users,
      companyMembers,
      projectMembers,
      projectRoleMembers,
      projectName,
      companyRoles,
      thisUser,
      company,
    } = this.props;
    const isSupport = isCompanyOwnerOrSupport(thisUser, company);
    const isImplicitTab = activeTab === 'implicit';
    const projectUsers = Object.values(this.props.users)
      .filter((user) => {
        const includeUser = Object.keys(projectMembers).includes(
          user!.id.toString()
        );
        const isImplicitUser = Boolean(projectMembers[`${user!.id}`]?.implicit);
        const implicitCheck =
          activeTab === 'batchAdd'
            ? true
            : isImplicitTab
              ? isImplicitUser
              : !isImplicitUser;
        return includeUser && implicitCheck;
      })
      .sort((a: IUser, b: IUser) => {
        const sortByName = a.name.localeCompare(b.name);
        return sortByName || 0;
      });

    const projectRoles = Object.values(this.props.companyRoles)
      .filter((role) => {
        const includeRole = Object.keys(projectRoleMembers).includes(
          role.id.toString()
        );
        const isImplicitRole = Boolean(
          projectRoleMembers[`${role.id}`]?.implicit
        );
        const implicitCheck =
          activeTab === 'batchAdd'
            ? true
            : isImplicitTab
              ? isImplicitRole
              : !isImplicitRole;
        return includeRole && implicitCheck;
      })
      .sort((a, b) => {
        return a.order! - b.order!;
      });

    const companyRolesOptions = Object.values(companyRoles).reduce(
      (temp: any, roleItem: ICompanyRole | undefined) => {
        if (!roleItem || roleItem.isProjectRole) return temp;
        temp.push({
          value: roleItem.id,
          label: roleItem.displayName,
        });
        return temp;
      },
      []
    );

    const title = projectRoutesName('members', projectName);
    return (
      <React.Fragment>
        <Helmet>
          <title>{title}</title>
          <meta name="s" content={title} />
        </Helmet>
        <h5>{title}</h5>
        <ProjectMembersHeader>
          {isSupport && (
            <ProjectMembersSelect
              roles={companyRoles}
              users={users}
              companyMembers={companyMembers}
              onSelectUser={this.handleSelectUser}
              onBatchAdd={this.handleBatchAdd}
            />
          )}
          <Select
            multiChoice={false}
            options={companyRolesOptions}
            values={[]}
            onChangeValues={this.handleSelectRole}
            placeholder={
              i18n.t('addRole', { ns: 'companyManagement' }) as string
            }
            label={i18n.t('addRole', { ns: 'companyManagement' }) as string}
          />
          <APopover
            target={
              <Button color={'blue-outline'} style={{ marginLeft: '' }}>
                {
                  i18n.t('memberAddRemoveTutorial', {
                    ns: 'companyManagement',
                  }) as string
                }{' '}
              </Button>
            }
          >
            <QuestionHintBox>
              <HintIcon type="info" />
              <HintText>
                <div>
                  {
                    i18n.t('memberAddRemoveTutorialText', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </div>
              </HintText>
            </QuestionHintBox>
          </APopover>
        </ProjectMembersHeader>
        {projectUsers ? (
          <ProjectMembersWrapper>
            <ProjectMembersTabs isLast={isImplicitTab}>
              {projectMemberItemTabs
                .filter((it) => it !== 'batchAdd' || batchAddInfo.length > 0)
                .map((tab) => (
                  <ProjectTabItem
                    key={tab}
                    isActive={tab === activeTab}
                    onClick={this.handleChangeActiveTab(tab)}
                  >
                    {
                      i18n.t(`${[tab]}`, {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </ProjectTabItem>
                ))}
            </ProjectMembersTabs>
            {activeTab === 'batchAdd' ? (
              <ProjectMemberBatchAdd
                roles={companyRoles}
                onUpdateNewUsers={this.handleBatchAdd}
                users={users}
                companyMembers={companyMembers}
                newUsers={batchAddInfo}
                projectRoles={projectRoles}
                projectUsers={projectUsers}
                addOrUpdateNewMembers={this.handleSubmitBatchAdd}
              />
            ) : (
              <ProjectMembersListWrapper>
                <h5>
                  {
                    i18n.t('memberRoles', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </h5>
                <>
                  {projectRoles.length === 0 ? (
                    <NoUserExists>
                      {messages.thereIsNoRoleYet(isImplicitTab)}
                    </NoUserExists>
                  ) : (
                    projectRoles.map((role) => (
                      <ProjectMemberView
                        roles={companyRoles}
                        companyMembers={companyMembers}
                        key={role.id}
                        isRole={true}
                        users={users}
                        user={role as ICompanyRole}
                        member={projectRoleMembers[role.id.toString()]}
                        onDelete={this.handleDeleteMember('roleId')}
                        onUpdateMemberStatus={this.handleUpdateMemberStatus(
                          projectRoleMembers
                        )}
                        notOwnerButIsProjectAdmin={false}
                        project={this.props.project}
                      />
                    ))
                  )}
                </>
                <h5>
                  {
                    i18n.t('memberUsers', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </h5>
                <>
                  {projectUsers.length === 0 ? (
                    <NoUserExists>
                      {messages.thereIsNoMemberYet(isImplicitTab)}
                    </NoUserExists>
                  ) : (
                    projectUsers.map((user: any) => (
                      // @ts-expect-error it must be ok
                      <ProjectMemberView
                        roles={companyRoles}
                        companyMembers={companyMembers}
                        key={user!.id}
                        user={user as IUser}
                        member={projectMembers[user!.id.toString()]}
                        projectRoleMembers={projectRoleMembers}
                        updateMember={(member) =>
                          this.props.updateMember(member, company.id)
                        }
                        addMember={(member) =>
                          this.props.addMember(member, company.id)
                        }
                        users={users}
                        isRole={false}
                        onDelete={this.handleDeleteMember('userId')}
                        onUpdateMemberStatus={this.handleUpdateMemberStatus(
                          this.props.projectMembers
                        )}
                        notOwnerButIsProjectAdmin={
                          thisUser.userId === user.id.toString() &&
                          notOwnerButIsProjectAdmin
                        }
                        project={this.props.project}
                      />
                    ))
                  )}
                </>
              </ProjectMembersListWrapper>
            )}
          </ProjectMembersWrapper>
        ) : null}
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const projectId = Number(props.projectId);
  const companyId = Number(props.companyId);
  const company = state.companies.data[companyId];
  const companyMembers = company.members;
  const companyRoles = company.roles;
  const companyOwnerId = company.owner.userId;
  const project = company.projects[projectId];
  const projectMembers = project.projectMembers;
  const myMembership = project.myMembership;
  const projectRoleMembers = project.projectRoleMembers;
  const projectName = project.name;
  const users = state.users.map;
  const thisUser = state.thisUser;

  return {
    companyMembers,
    companyRoles,
    companyOwnerId,
    projectMembers,
    myMembership,
    projectRoleMembers,
    projectName,
    thisUser,
    users,
    project,
    company,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    addMember: (projectMember: IProjectMemberView, companyId: number) =>
      appDispatch(addProjectMember({ projectMember, companyId })),
    removeMember: (projectMember: IProjectMemberView, companyId: number) =>
      appDispatch(removeProjectMember({ projectMember, companyId })),
    updateMember: (projectMember: IProjectMemberView, companyId: number) =>
      appDispatch(updateProjectMember({ projectMember, companyId })),
    requireUsers: (usersList: number[]) =>
      appDispatch(requireSeveralUser({ usersList })),
    getProjectData: (projectId, companyId: number) =>
      appDispatch(getProject({ projectId, companyId })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectMembersList);
